import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import '@/App.css';
import RouteSeo from './components/Seo';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import CVCheckerPage from './pages/CVCheckerPage';
import CVEntryPage from './pages/CVEntryPage';
import CVBuilderPage from './pages/CVBuilderPage';
import CommunityPage from './pages/CommunityPage';
import AffiliatePage from './pages/AffiliatePage';
import SuccessStoriesPage from './pages/SuccessStoriesPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import PaymentPolicyPage from './pages/PaymentPolicyPage';
import InterviewTestPage from './pages/InterviewTestPage';
import PricingPage from './pages/PricingPage';
import AccountPage from './pages/AccountPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminAccessPage from './pages/AdminAccessPage';
import B2BPage from './pages/B2BPage';
import AboutPage from './pages/AboutPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ContactPage from './pages/ContactPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import InterviewPackPage from './pages/InterviewPackPage';
import JobApplyKitPage from './pages/JobApplyKitPage';
import PartnersPage from './pages/PartnersPage';
import SalaryInsightPage from './pages/SalaryInsightPage';
import LinkedInTipsPage from './pages/LinkedInTipsPage';
import MockInterviewPage from './pages/MockInterviewPage';
import VoiceInterviewPage from './pages/VoiceInterviewPage';
import CareerCoachPage from './pages/CareerCoachPage';
import JobTrackerPage from './pages/JobTrackerPage';
import CareerToolsPage from './pages/CareerToolsPage';
import { Toaster } from './components/ui/sonner';
import { trackPageView } from './lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location]);
  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#CE1126]"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, fetchUser }}>
      <HelmetProvider>
      <BrowserRouter>
        <RouteSeo />
        <PageViewTracker />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/career-tools" element={<CareerToolsPage />} />
          <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <LoginPage />} />
          <Route path="/register" element={user ? <Navigate to="/dashboard" /> : <RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/checkout-success" element={user ? <CheckoutSuccessPage /> : <Navigate to="/login" />} />
          <Route path="/b2b" element={<B2BPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/salary" element={<SalaryInsightPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/dashboard" element={user ? (user.role === 'admin' ? <Navigate to="/admin" replace /> : <DashboardPage />) : <Navigate to="/login" />} />
          <Route path="/cv" element={user ? <CVEntryPage /> : <Navigate to="/login" />} />
          <Route path="/cv-checker" element={user ? <CVCheckerPage /> : <Navigate to="/login" />} />
          <Route path="/cv-builder" element={user ? <CVBuilderPage /> : <Navigate to="/login" />} />
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/affiliate" element={<AffiliatePage />} />
          <Route path="/referral-partner" element={<AffiliatePage />} />
          <Route path="/success-stories" element={<SuccessStoriesPage />} />
          <Route path="/refund" element={<RefundPolicyPage />} />
          <Route path="/payment-policy" element={<PaymentPolicyPage />} />
          <Route path="/interview-test" element={user ? <InterviewTestPage /> : <Navigate to="/login" />} />
          <Route path="/interview-pack" element={user ? <InterviewPackPage /> : <Navigate to="/login" />} />
          <Route path="/job-apply-kit" element={user ? <JobApplyKitPage /> : <Navigate to="/login" />} />
          <Route path="/linkedin-tips" element={user ? <LinkedInTipsPage /> : <Navigate to="/login" />} />
          <Route path="/ai/mock-interview" element={user ? <MockInterviewPage /> : <Navigate to="/login" />} />
          <Route path="/ai/voice-interview" element={user ? <VoiceInterviewPage /> : <Navigate to="/login" />} />
          <Route path="/ai/coach" element={user ? <CareerCoachPage /> : <Navigate to="/login" />} />
          <Route path="/tracker" element={user ? <JobTrackerPage /> : <Navigate to="/login" />} />
          <Route path="/account" element={user ? <AccountPage /> : <Navigate to="/login" />} />
          <Route path="/admin" element={user?.role === 'admin' ? <AdminDashboard /> : <Navigate to="/dashboard" />} />
          <Route path="/admin-access" element={user?.role === 'admin' ? <AdminAccessPage /> : <Navigate to="/dashboard" />} />
        </Routes>
      </BrowserRouter>
      </HelmetProvider>
      <Toaster position="bottom-right" />
    </AuthContext.Provider>
  );
}

export default App;
